import React, { useEffect, useState } from 'react';
import useIntegration from 'hooks/queries/useIntegration';
import {
  CardHeader,
  CardHeaderSubtitle,
  CardHeaderTitle
} from 'components/card/card-header';
import { FormattedMessage } from 'react-intl';
import messages from 'messages';
import { CardBody, CardFooter } from 'components/card';
import { Button, ButtonsGroup } from 'components/button';
import SyncPersonsWithIntegrationReviewTable from 'containers/pages/persons/components/SyncPersonsWithIntegrationReviewTable';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';
import { useMutation } from '@apollo/client';
import { MUTATION_ADD_EXTERNAL_PERSON_MAPPING } from 'services/aws/integration-query';
import useFetchAndFilterPersonsPool from 'hooks/queries/useFetchAndFilterPersonsPool';
import { format } from 'date-fns';
import Person from 'models/Person';

const SyncPersonsWithIntegrationCard = ({
  rootEntityId,
  entityId,
  integration,
  entityName,
  onClose,
  onStateChange
}) => {
  const persons = useEntityContext(s => s.persons);

  const [externalPersons, setExternalPersons] = useState([]);
  const [syncedPersons, setSyncedPersons] = useState([]);
  const [unSyncedPersons, setUnSyncedPersons] = useState([]);

  const { getExternalPersons } = useIntegration({
    integration
  });

  const { fetchPool } = useFetchAndFilterPersonsPool({
    entityId
  });

  const onSelectPerson = (person, externalPersonId) => {
    setExternalPersons(prevPersons => {
      return prevPersons.map(p => {
        return p.externalPersonId === externalPersonId
          ? { ...p, personId: person.personId, status: 'match' }
          : p;
      });
    });
  };

  const [addExternalPersonMapping] = useMutation(
    MUTATION_ADD_EXTERNAL_PERSON_MAPPING
  );

  useEffect(() => {
    const fetchPersons = async () => {
      const { syncedPersons, unsyncedPersons } = persons.reduce(
        (acc, person) => {
          if (person.integrations?.[integration]) {
            acc.syncedPersons.push(person);
          } else {
            acc.unsyncedPersons.push(person);
          }
          return acc;
        },
        { syncedPersons: [], unsyncedPersons: [] }
      );

      // setSyncedPersons(syncedPersons);
      setUnSyncedPersons(unsyncedPersons);

      let externalPersons = [];
      try {
        externalPersons = await getExternalPersons({ entityId });
      } catch (error) {
        console.log(error);
        onStateChange({
          type: 'error',
          payload: {
            message: `Error fetching persons for integration ${integration}: ${error.message}`
          }
        });
      }

      const syncedPersonsWithIntegration = syncedPersons.map(sp => {
        const extPerson = externalPersons.find(
          ep =>
            ep.externalPersonId ===
            sp.integrations?.[integration]?.externalPersonId
        );
        if (extPerson) {
          let newIntegrations = { ...sp.integrations };
          newIntegrations[integration] = {
            ...newIntegrations[integration],
            meta: extPerson
          };
          return new Person({
            ...sp,
            integrations: {
              ...newIntegrations
            }
          });
        }
        return sp;
      });
      console.log({ syncedPersonsWithIntegration });
      setSyncedPersons(syncedPersonsWithIntegration);

      // filter out persons where personId is in syncedPersons
      const filteredExternalPersons = externalPersons.filter(
        ip =>
          !syncedPersons.some(
            sp =>
              sp.integrations?.[integration]?.externalPersonId ===
              ip.externalPersonId
          )
      );

      if (filteredExternalPersons.length > 0) {
        // Match IntegrationPersons with local persons by firstname and lastname,
        // and if possible by birthday or email,
        // if a match is found, add the local person id as a prop to the IntegrationPersons array
        const matchedPersons = filteredExternalPersons.map(externalPerson => {
          const match = unsyncedPersons.find(
            localPerson =>
              externalPerson.firstname === localPerson.firstname &&
              externalPerson.lastname === localPerson.lastname &&
              ((externalPerson.birthday &&
                localPerson.birthday &&
                externalPerson.birthday === localPerson.birthday) ||
                (externalPerson.email &&
                  localPerson.email &&
                  externalPerson.email === localPerson.email) ||
                (!externalPerson.birthday && !externalPerson.email))
          );
          return match
            ? {
                ...externalPerson,
                personId: match.id,
                status: 'match'
              }
            : externalPerson;
        });

        setExternalPersons(matchedPersons);
        onStateChange({
          type: 'sync',
          payload: {
            persons,
            entityId,
            rootEntityId,
            integration,
            entityName
          }
        });
      } else {
        onStateChange({
          type: 'error',
          payload: {
            message: `No persons found for integration ${integration} in entity ${entityName}.`
          }
        });
      }
    };
    fetchPersons();
  }, [entityId, integration, persons]);

  const onConfirmPerson = async person => {
    await addExternalPersonMapping({
      variables: {
        personId: person.personId,
        externalPersonId: person.externalPersonId,
        entityId,
        integrationType: integration
      }
    });
    await fetchPool();
    console.log('Person synced successfully');
  };

  return (
    <>
      <CardHeader modal>
        <CardHeaderTitle>
          <FormattedMessage
            {...messages.integrationSyncPersonsTitle}
            values={{
              integration,
              b: chunks => (
                <span className="c-card__header-highlight">{chunks}</span>
              )
            }}
          />
        </CardHeaderTitle>

        <CardHeaderSubtitle>
          <FormattedMessage
            {...messages.integrationSyncPersonsSubtitle}
            values={{
              integration
            }}
          />
        </CardHeaderSubtitle>
      </CardHeader>
      <CardBody modal>
        <div className="c-file-upload--wrapper-states u-padding-top-medium">
          <p className="c-file-upload__message c-file-upload__message-success">
            <span>
              <FormattedMessage
                {...messages.integrationSyncInfoAll}
                values={{
                  integration,
                  count: externalPersons.length,
                  b: chunks => <b>{chunks}</b>
                }}
              />
            </span>
            <span>
              <FormattedMessage
                {...messages.integrationSyncInfoPossible}
                values={{
                  integration,
                  count: externalPersons.filter(p => p.personId).length,
                  b: chunks => <b>{chunks}</b>
                }}
              />
            </span>

            <span>
              <FormattedMessage
                {...messages.integrationSyncInfoConfirmed}
                values={{
                  integration,
                  count: syncedPersons.length,
                  b: chunks => <b>{chunks}</b>
                }}
              />
            </span>
          </p>

          {externalPersons.length > 0 && (
            <SyncPersonsWithIntegrationReviewTable
              integration={integration}
              syncedPersons={syncedPersons}
              externalPersons={externalPersons}
              persons={unSyncedPersons.map(p => {
                return {
                  value: p.id,
                  label: `${p.firstname} ${p.lastname} (${format(p.birthdate, 'dd/LL/yyyy')})`,
                  personId: p.id
                };
              })}
              selected={[]}
              onSelectPerson={onSelectPerson}
              onConfirmPerson={onConfirmPerson}
            />
          )}
        </div>
      </CardBody>
      <CardFooter modal>
        <ButtonsGroup fullwidth>
          <Button secondary onClick={onClose}>
            <FormattedMessage {...messages.buttonClose} />
          </Button>
        </ButtonsGroup>
      </CardFooter>
    </>
  );
};

export default SyncPersonsWithIntegrationCard;
